import { useIntl } from 'react-intl';
import { Bar, BarChart, ResponsiveContainer, XAxis, YAxis } from 'recharts';

import ComparisonBadge from 'src/features/home/components/ComparisonBadge';
import { HomeStatistics } from 'src/features/home/types';
import { homeMessages } from 'src/features/home/utils/messages';
import { GeneralBlock } from 'src/registry/components/blocks/GeneralBlock';
import { SectionBlockSubtitle, SectionBlockTitle } from 'src/registry/components/blocks/SectionBlock';
import StatisticValue from 'src/registry/components/statistic/StatisticValue';
import GeneralMessageBlock from 'src/registry/components/blocks/GeneralMessageBlock';

type Props = {
    totalOccupancy?: HomeStatistics['total_occupancy'];
    isLoading?: boolean;
    isError?: boolean;
};

const dummyData = [
    {
        title: '-',
        value: 0,
    },
    {
        title: '-',
        value: 0,
    },
    {
        title: '-',
        value: 0,
    },
];
export default function WidgetOccupancy({ totalOccupancy, isLoading, isError }: Props) {
    const { formatMessage } = useIntl();

    const data = totalOccupancy
        ? [
              { title: formatMessage(homeMessages.total), value: totalOccupancy?.total_spaces },
              { title: formatMessage(homeMessages.clients), value: totalOccupancy?.total_client_spaces },
              { title: formatMessage(homeMessages.members), value: totalOccupancy?.total_membership_spaces },
          ]
        : dummyData;

    return (
        <GeneralBlock className="flex flex-1 flex-col justify-between">
            <div className="flex flex-col">
                <SectionBlockTitle>{formatMessage(homeMessages.totalOccupancy)}</SectionBlockTitle>
                <SectionBlockSubtitle>{formatMessage(homeMessages.totalOccupancyDescription)}</SectionBlockSubtitle>
            </div>
            {isError ? (
                <GeneralMessageBlock variant="error" />
            ) : (
                <>
                    <div>
                        <div className="flex w-full justify-center gap-5 text-xs">
                            <StatisticValue
                                title={formatMessage(homeMessages.totalSpaces)}
                                value={totalOccupancy?.currently_occupied_spaces}
                                totalValue={totalOccupancy?.total_spaces}
                                isLoading={isLoading}
                            />
                            <StatisticValue
                                title={formatMessage(homeMessages.clientSpaces)}
                                value={totalOccupancy?.currently_occupied_client_spaces}
                                totalValue={totalOccupancy?.total_client_spaces}
                                isLoading={isLoading}
                            />
                            <StatisticValue
                                title={formatMessage(homeMessages.memberSpaces)}
                                value={totalOccupancy?.currently_occupied_membership_spaces}
                                totalValue={totalOccupancy?.total_membership_spaces}
                                isLoading={isLoading}
                            />
                        </div>
                        <ResponsiveContainer minHeight={100}>
                            <BarChart data={data} layout="vertical">
                                <XAxis type="number" hide />
                                <YAxis type="category" dataKey="title" hide />
                                <Bar
                                    dataKey="value"
                                    radius={4}
                                    background={{ radius: 4, color: '#EAECF0' }}
                                    fill="#026AA2"
                                    label={(props) => renderCustomBarLabel({ data, ...props } as CustomBarLabelProps)}
                                />
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                    <ComparisonBadge value={totalOccupancy?.compared_to_last_day} />
                </>
            )}
        </GeneralBlock>
    );
}

interface CustomBarLabelProps {
    data: {
        title: string;
        value?: number;
    }[];
    y?: number;
    index?: number;
    height?: number;
    value?: number;
    parentViewBox?: {
        width: number;
        height: number;
        x: number;
        y: number;
    };
}

const renderCustomBarLabel = (props: CustomBarLabelProps) => {
    const {
        y = 0,
        height = 0,
        value = 0,
        index = 0,
        parentViewBox = {
            x: 0,
            y: 0,
            height: 0,
            width: 0,
        },
        data,
    } = props;

    const centerY = y + height / 2;
    const endX = parentViewBox.x + parentViewBox.width;
    const total = data[0].value ?? 0;
    const minimumColorValue = total * 0.2;

    return (
        <g>
            <text
                x={parentViewBox.x + 16}
                y={centerY}
                textAnchor="start"
                dominantBaseline="middle"
                fill={minimumColorValue <= value && total !== 0 ? '#FFF' : '#101828'}
                className="text-xs font-medium"
            >
                {data[index].title}
            </text>

            <text
                x={endX - 16}
                y={centerY}
                textAnchor="end"
                dominantBaseline="middle"
                fill="#101828"
                className="text-xs font-medium"
            >
                {value}
            </text>
        </g>
    );
};
